import React from 'react'
import { dayjs } from '@mobi/utils/date'
import { TicketDisplayContext, TicketDisplayContextType } from '@core/Areas/AccountActivity/context'
import { BetInfo } from '@core/Areas/AccountActivity/Components/Ticket/Components/BetInfo'
import { SelectionInfo } from '@core/Areas/AccountActivity/Components/Ticket/Components/Details/Components/SelectionInfo'
import { getTicketLayoutType } from '@core/Areas/AccountActivity/Components/Ticket/helpers/legTypes'
import type { BetLoopShareTicket, BetLoopSharedBet } from '@core/Areas/BetLoop/types'
import { trackBetLoopExpandSelection } from '@core/Areas/BetLoop/helpers/analytics'
import { getBetLoopTicketStatus } from '@core/Areas/BetLoop/helpers/getBetLoopTicketStatus'
import { HeightTruncateWrapper } from './Components/HeightTruncateWrapper'
import { BetSelectionContainer } from '../Common/BetSelectionContainer'
import { useRaceClosedTopicSubscription } from './hooks/useRaceClosedTopicSubscription'

export const BetSelectionDisplay: React.FC<{
  ticket: BetLoopShareTicket
  isResulted: boolean
  onPendingStatusChange(): void
  shouldDisableHeightTruncate?: true
  isForExternalShare?: boolean
  postId?: BetLoopSharedBet['postId']
}> = ({
  ticket,
  isResulted,
  onPendingStatusChange,
  shouldDisableHeightTruncate,
  isForExternalShare,
  postId,
}) => {
  const ticketTypeLayout = getTicketLayoutType(ticket)

  const handleOnChangeCallback = (expanded: boolean) => {
    trackBetLoopExpandSelection(expanded ? 'enable' : 'disable')
  }

  const ticketResultStatus = getBetLoopTicketStatus(ticket)
  const winShareResult = isResulted && ticketResultStatus === 'Won'
  const isPending = ticketResultStatus === 'Pending'
  const noPayoutShareResult = isResulted && ticketResultStatus !== 'Won'
  const hasMultipleLegToteBetStarted =
    (!isResulted &&
      ticket.allUpStartTimes?.some(ast => dayjs(ast).isBefore(dayjs().subtract(2, 'minute')))) ||
    false

  const contextValue = React.useMemo<TicketDisplayContextType>(() => {
    const forcedStatus =
      (isResulted && isPending) || hasMultipleLegToteBetStarted
        ? 'pending'
        : isResulted
          ? 'resulted'
          : undefined

    return {
      type: 'share',
      data: {
        shouldHideCost: true,
        display: isForExternalShare ? 'external-share' : 'bet-loop',
        forcedStatus,
      },
    }
  }, [isResulted, isForExternalShare, isPending, hasMultipleLegToteBetStarted])

  const forcedStatus = contextValue.type === 'share' && contextValue.data.forcedStatus
  React.useEffect(() => {
    if (forcedStatus === 'pending') {
      onPendingStatusChange()
    }
  }, [onPendingStatusChange, forcedStatus])

  useRaceClosedTopicSubscription({
    ticket,
    postId,
    shouldForceDisable: !!forcedStatus,
  })

  return (
    <BetSelectionContainer.Container
      displayStatus={winShareResult ? 'win' : noPayoutShareResult ? 'loss' : null}
    >
      <HeightTruncateWrapper
        ticket={ticket}
        onChangeCallback={handleOnChangeCallback}
        shouldEnableHeightTruncate={!shouldDisableHeightTruncate}
      >
        <TicketDisplayContext.Provider value={contextValue}>
          <BetSelectionContainer.Inner>
            <BetInfo
              ticket={ticket}
              status={ticketResultStatus}
              ticketLayoutType={ticketTypeLayout}
            />

            <SelectionInfo ticket={ticket} />
          </BetSelectionContainer.Inner>
        </TicketDisplayContext.Provider>
      </HeightTruncateWrapper>
    </BetSelectionContainer.Container>
  )
}
